import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import TopBar from "@/components/TopBar";
import BottomNav from "@/components/BottomNav";
import { Shield, Mail, Lock, KeyRound, AtSign, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function Section({ icon: Icon, title, desc, children, testId }) {
  return (
    <div className="card-surface p-5" data-testid={testId}>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-emerald-50 text-emerald-700 grid place-items-center shrink-0">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold">{title}</div>
          {desc && <div className="text-xs text-zinc-500 mt-0.5">{desc}</div>}
        </div>
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

const inputCls = "w-full h-11 rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none px-3 text-sm";

export default function AdminSecurity() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const [info, setInfo] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [em, setEm] = useState({ new_email: "", current_password: "" });
  const [tfa, setTfa] = useState({ enabled: true, current_password: "" });
  const [busy, setBusy] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/admin/security");
      setInfo(data);
      setTfa((s) => ({ ...s, enabled: data.tfa_enabled }));
      setEm((s) => ({ ...s, new_email: data.owner_email || "" }));
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) { toast.error("New passwords don't match"); return; }
    if (pw.new_password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy((b) => ({ ...b, pw: true }));
    try {
      await api.post("/admin/security/password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password changed. Please sign in again.");
      await logout();
      window.location.href = "/login";
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy((b) => ({ ...b, pw: false })); }
  };

  const changeEmail = async (e) => {
    e.preventDefault();
    setBusy((b) => ({ ...b, em: true }));
    try {
      const { data } = await api.post("/admin/security/owner-email", { new_email: em.new_email, current_password: em.current_password });
      toast.success("Owner email updated to " + data.owner_email);
      setEm({ new_email: data.owner_email, current_password: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy((b) => ({ ...b, em: false })); }
  };

  const toggleTfa = async (e) => {
    e.preventDefault();
    setBusy((b) => ({ ...b, tfa: true }));
    try {
      const { data } = await api.post("/admin/security/tfa", { enabled: tfa.enabled, current_password: tfa.current_password });
      toast.success(`2FA ${data.tfa_enabled ? "enabled" : "disabled"}`);
      setTfa({ enabled: data.tfa_enabled, current_password: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy((b) => ({ ...b, tfa: false })); }
  };

  if (!info) return <div className="app-shell p-5">…</div>;

  return (
    <div className="app-shell pb-24" data-testid="admin-security-page">
      <TopBar title="Owner & Security" />
      <div className="px-5 pt-4 space-y-4">
        <div className="card-surface p-4 bg-gradient-to-br from-emerald-50 to-white border-emerald-200" data-testid="owner-card">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-2xl bg-emerald-600 text-white grid place-items-center">
              <Shield className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-700">Owner identity</div>
              <div className="font-display text-lg font-bold mt-0.5">{user?.name || "Admin"}</div>
              <div className="text-sm text-zinc-700">{user?.email}</div>
              <div className="text-xs text-zinc-500 mt-1 inline-flex items-center gap-1">
                <Mail className="h-3 w-3" /> Owner email: <b>{info.owner_email || "—"}</b>
              </div>
              <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-1 text-xs font-semibold">
                <CheckCircle2 className="h-3 w-3" /> Verified owner
              </div>
            </div>
          </div>
        </div>

        <Section icon={Lock} title="Change password" desc="Set a strong new password (8+ chars). You'll be signed out after change." testId="section-password">
          <form onSubmit={changePassword} className="space-y-3">
            <input
              data-testid="pw-current"
              type="password"
              required
              placeholder="Current password"
              value={pw.current_password}
              onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
              className={inputCls}
            />
            <input
              data-testid="pw-new"
              type="password"
              required
              placeholder="New password"
              minLength={8}
              value={pw.new_password}
              onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
              className={inputCls}
            />
            <input
              data-testid="pw-confirm"
              type="password"
              required
              placeholder="Confirm new password"
              value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              className={inputCls}
            />
            <button data-testid="pw-submit" disabled={busy.pw} className="btn-primary w-full">{busy.pw ? "..." : "Update password"}</button>
          </form>
        </Section>

        <Section icon={AtSign} title="Owner email" desc="Email that receives owner notifications and admin OTP codes." testId="section-owner-email">
          <form onSubmit={changeEmail} className="space-y-3">
            <input
              data-testid="em-new"
              type="email"
              required
              placeholder="new-owner@example.com"
              value={em.new_email}
              onChange={(e) => setEm({ ...em, new_email: e.target.value })}
              className={inputCls}
            />
            <input
              data-testid="em-current-password"
              type="password"
              required
              placeholder="Current password"
              value={em.current_password}
              onChange={(e) => setEm({ ...em, current_password: e.target.value })}
              className={inputCls}
            />
            <button data-testid="em-submit" disabled={busy.em} className="btn-primary w-full">{busy.em ? "..." : "Update owner email"}</button>
          </form>
        </Section>

        <Section icon={KeyRound} title="Two-factor authentication" desc="Email OTP code required on every admin sign-in. Sent to the owner email." testId="section-tfa">
          <form onSubmit={toggleTfa} className="space-y-3">
            <label className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 p-3 cursor-pointer">
              <div>
                <div className="font-semibold text-sm">Require OTP at login</div>
                <div className="text-xs text-zinc-500">Recommended</div>
              </div>
              <input
                data-testid="tfa-toggle"
                type="checkbox"
                checked={tfa.enabled}
                onChange={(e) => setTfa({ ...tfa, enabled: e.target.checked })}
                className="h-5 w-5 accent-emerald-600"
              />
            </label>
            <input
              data-testid="tfa-current-password"
              type="password"
              required
              placeholder="Current password"
              value={tfa.current_password}
              onChange={(e) => setTfa({ ...tfa, current_password: e.target.value })}
              className={inputCls}
            />
            <button data-testid="tfa-submit" disabled={busy.tfa} className="btn-primary w-full">{busy.tfa ? "..." : "Save 2FA settings"}</button>
          </form>
        </Section>
      </div>
      <BottomNav />
    </div>
  );
}
